import Tippy, { TippyProps } from '@tippyjs/react'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Instance } from 'tippy.js'

import { addEventListener } from '~lib/helpers'

const hideOnPopperBlur = {
  name: 'hideOnPopperBlur',
  defaultValue: true,
  fn(instance) {
    return {
      onCreate() {
        instance.popper.addEventListener('focusout', (event) => {
          if (
            instance.props.hideOnPopperBlur &&
            event.relatedTarget &&
            !instance.popper.contains(event.relatedTarget)
          ) {
            instance.hide()
          }
        })
      },
    }
  },
}

const defaultOffset = [0, 8] as TippyProps['offset']

type PartialTippyProps = Omit<TippyProps, 'interactive' | 'animation'>

export interface PopupProps extends PartialTippyProps {
  animation?: 'fade' | 'scale'
  preventOverflow?: boolean
  flip?: boolean
  hideOnEscape?: boolean
  renderContent?: (tippy: Instance) => JSX.Element
  lockBody?: boolean
}

export type SharedPopupProps = Pick<PopupProps, 'offset' | 'preventOverflow' | 'flip' | 'appendTo' | 'placement'>

const Popup: FC<React.PropsWithChildren<PopupProps>> = ({
  children,
  offset = defaultOffset,
  animation = 'fade',
  preventOverflow = false,
  flip = false,
  hideOnEscape = true,
  zIndex = 999999,
  maxWidth = 'none',
  lockBody = false,
  onShow,
  onHide,
  onHidden,
  onCreate,
  content,
  renderContent,
  ...props
}) => {
  const [tippy, setTippy] = useState<Instance>(null)
  const [mounted, setMounted] = useState(false)

  const handleCreate = useCallback(
    (instance?: Instance) => {
      onCreate?.(instance)
      setTippy(instance)
    },
    [onCreate]
  )

  const handleShow = useCallback(
    (instance: Instance) => {
      if (lockBody) {
        document.body.classList?.add('locked')
        disableBodyScroll(instance.popper, { reserveScrollBarGap: true })
      }
      setMounted(true)
      onShow?.(instance)
    },
    [lockBody, onShow]
  )

  const handleHide = useCallback(
    (instance: Instance) => {
      if (lockBody) {
        document.body.classList?.remove('locked')
        enableBodyScroll(instance.popper)
      }
      onHide?.(instance)
    },
    [lockBody, onHide]
  )

  const handleHidden = useCallback(
    (instance: Instance) => {
      setMounted(false)
      onHidden?.(instance)
    },
    [onHidden]
  )

  const popperOptions = useMemo(
    () => ({
      modifiers: [
        { name: 'preventOverflow', enabled: preventOverflow },
        { name: 'flip', enabled: flip },
      ],
    }),
    [preventOverflow, flip]
  )

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (hideOnEscape && mounted) {
      return addEventListener(window, 'keydown', (ev) => {
        if (ev.code === 'Escape' && hideOnEscape) {
          if (tippy) {
            tippy.hide()
          }
          // setTippy(null)
          setMounted(false)
        }
      })
    }
  }, [hideOnEscape, mounted, tippy])

  const computedContent = mounted ? renderContent?.(tippy) || content : null

  return (
    <Tippy
      interactive
      offset={offset}
      popperOptions={popperOptions}
      animation={animation}
      zIndex={zIndex}
      maxWidth={maxWidth}
      onShow={handleShow}
      onHide={handleHide}
      onHidden={handleHidden}
      onCreate={handleCreate}
      content={computedContent}
      plugins={[hideOnPopperBlur]}
      {...props}
    >
      {children}
    </Tippy>
  )
}

export default memo(Popup)
